import { useRef, useState } from "react";
import { Upload, RefreshCw, FileText, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { uploadInChunks } from "@/lib/chunkedUpload";
import { maybeCompressImage } from "@/lib/compressImage";
import { toast } from "@/components/ui/sonner";
import { useLanguage } from "@/context/LanguageContext";

/**
 * 2026-07-30 · 询价文件识别 panel — customer RFQ upload + AI auto-fill.
 *
 * Only surfaced on the "新建产品" page (isEditing=false). Toggle button in
 * the parent controls visibility so users who don't have an RFQ never see
 * this panel. When a file is uploaded, the panel calls
 * ``POST /api/ai/extract-rfq`` and passes the extracted fields back to the
 * parent via ``onExtracted`` so the product form auto-fills.
 *
 * Supports Excel (.xlsx / .xls / .xlsm), PDF, and image formats. For image
 * RFQs the whole image is also set as the product photo (natural for phone
 * snapshots of a customer spec sheet).
 */
export function RfqPanel({ onExtracted }) {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const runExtraction = async (f) => {
    if (!f) return;
    setBusy(true);
    setDone(false);
    try {
      const compressed = await maybeCompressImage(f);
      const uploadId = await uploadInChunks(compressed);
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("orig_filename", compressed.name);
      const { data } = await api.post("/ai/extract-rfq", fd, { timeout: 180000 });
      onExtracted?.(data);
      setDone(true);
      toast.success(t("rfq.extracted") || "识别完成");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || (t("rfq.error") || "识别失败"));
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    // Local preview URL for images only — PDF/Excel show a filename card.
    if (/^image\//.test(f.type)) setPreviewUrl(URL.createObjectURL(f));
    else setPreviewUrl(null);
    await runExtraction(f);
  };

  const kind = file
    ? (/^image\//.test(file.type) ? "image"
       : file.name.toLowerCase().endsWith(".pdf") ? "pdf"
       : /\.(xlsx|xls|xlsm|csv)$/i.test(file.name) ? "excel"
       : /\.(pptx|ppt)$/i.test(file.name) ? "ppt"
       : "other")
    : null;

  return (
    <aside
      className="w-full lg:w-[420px] shrink-0 bg-card border border-border rounded-lg shadow-sm p-6 space-y-5 h-fit lg:sticky lg:top-4"
      data-testid="rfq-panel"
    >
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={16} className="text-brand" />
          <h3 className="font-semibold text-sm">{t("rfq.panelTitle") || "询价文件识别"}</h3>
        </div>
        {done && (
          <span className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-0.5">
            <CheckCircle2 size={12} /> {t("rfq.done") || "识别完成"}
          </span>
        )}
      </header>

      {/* Step 1 — upload */}
      <section className="space-y-2">
        <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
          1. {t("rfq.stepUpload") || "上传客户询价文件"}
        </p>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.xlsm,.csv,.pdf,.pptx,.ppt,image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="rfq-file-input"
        />
        {!file ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-full h-24 rounded-md border-2 border-dashed border-border hover:border-brand hover:bg-brand/5 flex flex-col items-center justify-center gap-1.5 text-xs text-muted-foreground transition-colors"
            data-testid="rfq-upload-btn"
          >
            <Upload size={18} />
            <span>{t("rfq.dropHere") || "点击上传 Excel / PDF / 图片"}</span>
          </button>
        ) : (
          <div className="rounded-md border border-border bg-muted/30 p-3 flex items-center gap-3" data-testid="rfq-file-card">
            <FileText size={20} className="text-brand shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate" data-testid="rfq-file-name">{file.name}</p>
              <p className="text-[10px] text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
            {busy && <Loader2 size={16} className="animate-spin text-brand shrink-0" />}
          </div>
        )}
      </section>

      {/* Step 2 — preview */}
      {file && (
        <section className="space-y-2">
          <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            2. {t("rfq.stepPreview") || "询价文件预览"}
          </p>
          {kind === "image" && previewUrl && (
            <img
              src={previewUrl}
              alt="RFQ preview"
              className="w-full rounded-md border border-border object-contain max-h-[360px] bg-white"
              data-testid="rfq-image-preview"
            />
          )}
          {(kind === "pdf" || kind === "excel" || kind === "ppt" || kind === "other") && (
            <div className="rounded-md border border-border bg-muted/30 h-40 flex items-center justify-center text-xs text-muted-foreground text-center px-4" data-testid="rfq-no-preview">
              {kind === "pdf" && (t("rfq.pdfNoPreview") || "PDF 已上传（浏览器内无预览，AI 已在识别）")}
              {kind === "excel" && (t("rfq.excelNoPreview") || "Excel 已上传（AI 已在识别）")}
              {kind === "ppt" && (t("rfq.pptNoPreview") || "PowerPoint 已上传（AI 已在识别）")}
              {kind === "other" && (t("rfq.unknownType") || "未知格式")}
            </div>
          )}
        </section>
      )}

      {/* Action row */}
      {file && (
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="flex-1 h-9 rounded-md border border-border text-xs font-medium hover:bg-accent disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
            data-testid="rfq-reupload-btn"
          >
            <Upload size={13} /> {t("rfq.reupload") || "重新上传"}
          </button>
          <button
            type="button"
            onClick={() => runExtraction(file)}
            disabled={busy}
            className="flex-1 h-9 rounded-md bg-brand text-white text-xs font-medium hover:bg-brand/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
            data-testid="rfq-reextract-btn"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            {t("rfq.reextract") || "重新识别"}
          </button>
        </div>
      )}
    </aside>
  );
}
